import { useRef } from "react";
import { isRejectedConnectionOperation } from "@/lib/data/requests/connections/connection";

/** Keep a lost acknowledgement retry attached to the original receipt.
 * An acknowledgement, definitive rejection or changed intent starts a new operation.
 */
export function useOperationIntent() {
  const pending = useRef<{
    key: string;
    operationId: string;
    retryable: boolean;
  }>();
  const finish = (variables?: { operationId?: string }) => {
    if (
      pending.current &&
      (!variables || variables.operationId === pending.current.operationId)
    )
      pending.current.retryable = false;
  };
  return {
    onMutate: (variables: { operationId?: string }) => {
      const key = JSON.stringify(variables, (name, value: unknown) =>
        [
          "operationId",
          "dateModified",
          "currentVehicle",
          "currentInsurance",
        ].includes(name)
          ? undefined
          : value,
      );
      const replacement = variables as {
        replacement?: { operationId?: string };
      };
      if (
        !pending.current ||
        pending.current.key !== key ||
        !pending.current.retryable
      ) {
        const suppliedId =
          variables.operationId ?? replacement.replacement?.operationId;
        pending.current = {
          key,
          operationId:
            suppliedId && suppliedId !== pending.current?.operationId
              ? suppliedId
              : crypto.randomUUID(),
          retryable: true,
        };
      }
      variables.operationId = pending.current.operationId;
    },
    onError: (error: unknown, variables?: { operationId?: string }) => {
      if (isRejectedConnectionOperation(error)) finish(variables);
    },
    onSuccess: (_data?: unknown, variables?: { operationId?: string }) => {
      finish(variables);
    },
  };
}
